import cx from 'classnames';
import { useState } from 'react';

import { Icon } from '@sb/design-system';
import { useDevPushState } from '@sbrc/hooks';

/**
 * Signals that this CB is running custom dev-pushed code without claiming any
 * vertical layout space:
 *   1. A 4px viewport outline frames every page (pointer-events: none so it
 *      never intercepts clicks).
 *   2. A small persistent chip at the bottom-center, source-tagged via its
 *      testid (`dev-push-banner-scp` or `dev-push-banner-registry`), toggling
 *      a popover with the full message + exit instructions.
 *
 * Both elements are `position: fixed` overlays. They never push, pad, or
 * resize page content, so pages that pin themselves with `tw-fixed tw-inset-0`
 * (Move overlay, Robby assembly header, etc.) render unchanged underneath.
 */
export default function DevPushBanner() {
  const state = useDevPushState();
  const [expanded, setExpanded] = useState(false);

  if (state.source === null) return null;

  const isScp = state.source === 'scp';

  const chipTestId = isScp ? 'dev-push-banner-scp' : 'dev-push-banner-registry';

  const chipLabel = isScp ? `DEV PUSH (${state.services.length})` : 'DEV BUILD';

  const ariaLabel = isScp
    ? `Dev-push mode active. ${state.services.length} service${
        state.services.length === 1 ? '' : 's'
      } overridden: ${state.services.join(', ')}`
    : `Dev build active: ${state.buildName}`;

  return (
    <>
      <div
        data-testid="dev-push-banner-outline"
        aria-hidden="true"
        className={cx(
          'tw-fixed',
          'tw-inset-0',
          'tw-pointer-events-none',
          'tw-z-40',
        )}
        style={{
          border: '4px solid var(--blue-60, #265CF0)',
          boxSizing: 'border-box',
        }}
      />
      <button
        type="button"
        data-testid={chipTestId}
        onClick={() => setExpanded((prev) => !prev)}
        aria-expanded={expanded}
        aria-label={ariaLabel}
        className={cx(
          'tw-fixed',
          'tw-bottom-16',
          'tw-z-40',
          'tw-flex',
          'tw-items-center',
          'tw-gap-8',
          'tw-rounded-full',
          'tw-bg-blue-60',
          'tw-px-12',
          'tw-py-6',
          'tw-text-sm',
          'tw-font-medium',
          'tw-text-white',
          'tw-shadow-md',
          'hover:tw-bg-blue-70',
        )}
        style={{ left: '50%', transform: 'translateX(-50%)' }}
      >
        <Icon kind="settings" />
        <span>{chipLabel}</span>
      </button>
      {expanded && (
        <div
          data-testid="dev-push-banner-popover"
          role="dialog"
          className={cx(
            'tw-fixed',
            'tw-bottom-60',
            'tw-z-40',
            'tw-max-w-320',
            'tw-rounded-lg',
            'tw-bg-blue-60',
            'tw-px-16',
            'tw-py-12',
            'tw-text-sm',
            'tw-text-white',
            'tw-shadow-md',
          )}
          style={{ left: '50%', transform: 'translateX(-50%)' }}
        >
          {isScp ? (
            <>
              <p>
                This control box is running custom dev-pushed services:{' '}
                <strong>{state.services.join(', ')}</strong>.
              </p>
              <p className="tw-mt-8">
                Run{' '}
                <code className="tw-bg-blue-70 tw-px-4 tw-py-2 tw-rounded">
                  botctl dev-push exit
                </code>{' '}
                on this CB to revert to the installed build.
              </p>
            </>
          ) : (
            <>
              <p>
                This control box is running a dev build (
                <strong>{state.buildName}</strong>).
              </p>
              <p className="tw-mt-8">
                To revert, re-install the original build from Marvin (Manage
                Robots → Software → Install).
              </p>
            </>
          )}
        </div>
      )}
    </>
  );
}
